import React, { useState, useEffect, useCallback } from 'react';
import { 
  TextField, 
  Autocomplete, 
  CircularProgress, 
  Box, 
  Typography, 
  Chip,
  Alert
} from '@mui/material';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

/**
 * MasterJobSelector component for searching and selecting master jobs
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Function to call when a master job is selected
 * @param {number|null} props.value - Current selected master job ID
 * @param {string|null} props.kategori - Category filter (optional)
 */
const MasterJobSelector = ({ onChange, value, kategori }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Load all master jobs without pagination or filters
  const loadAllMasterJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all master jobs with a large limit to get everything
      let url = `/master-jobs?limit=1000`;
      
      // Add category filter if provided
      if (kategori) {
        url += `&kategori=${encodeURIComponent(kategori)}`;
      }
      
      console.log('Loading all master jobs with URL:', url);
      const response = await api.get(url);
      console.log('All master jobs response:', response.data);
      
      if (response.data && response.data.success) {
        setOptions(response.data.data);
        
        if (response.data.data.length === 0) {
          setError(t('TIDAK ADA ITEM PEKERJAAN MASTER YANG DITEMUKAN. SILAKAN TAMBAHKAN BEBERAPA DI MENU DAFTAR MASTER ITEM PEKERJAAN TERLEBIH DAHULU.'));
        }
      } else {
        setOptions([]);
        setError(t('GAGAL MEMUAT ITEM PEKERJAAN MASTER'));
      }
    } catch (error) {
      console.error('Error loading master jobs:', error);
      setOptions([]);
      setError('Connection error: Check that the backend is running at http://localhost:3000');
    } finally {
      setLoading(false);
    }
  }, [kategori]);

  // Load all master jobs on component mount
  useEffect(() => {
    loadAllMasterJobs();
  }, [loadAllMasterJobs]);
  
  // Load selected job details on initial render
  useEffect(() => {
    const fetchSelectedJob = async () => {
      if (value) {
        try {
          console.log('Fetching selected master job with ID:', value);
          const response = await api.get(`/master-jobs/${value}`);
          console.log('Selected master job response:', response.data);
          
          if (response.data && response.data.success) {
            setSelectedJob(response.data.data);
            
            // If options are empty, add this job to options
            if (options.length === 0 || !options.some(option => option.id === response.data.data.id)) {
              setOptions(prev => [...prev, response.data.data]);
            }
          }
        } catch (error) {
          console.error('Error fetching master job details:', error);
        }
      } else {
        setSelectedJob(null);
      }
    };
    
    fetchSelectedJob();
  }, [value, options.length, options]);
  
  
  const searchMasterJobs = useCallback(async (query) => {
    // Don't search if there's already a selected value
    if (selectedJob) return;
    
    setLoading(true);
    setError(null);
    try {
      let url = `/master-jobs/search/autocomplete?query=${encodeURIComponent(query)}&limit=50`;
      
      // Add category filter if provided
      if (kategori) {
        url += `&kategori=${encodeURIComponent(kategori)}`;
      }
      
      console.log('Searching master jobs with URL:', url);
      const response = await api.get(url);
      console.log('Search response:', response.data);
      
      if (response.data && response.data.success) {
        setOptions(response.data.data);
        
        if (response.data.data.length === 0 && !selectedJob) {
          setError(t('TIDAK ADA HASIL YANG DITEMUKAN UNTUK') + ` "${query}"`);
        }
      } else {
        // Don't clear options if there's a selected job
        if (!selectedJob) {
          setOptions([]);
          setError(t('GAGAL MENCARI ITEM PEKERJAAN MASTER'));
        }
      }
    } catch (error) {
      console.error('Error searching master jobs:', error);
      // Don't clear options if there's a selected job
      if (!selectedJob) {
        setOptions([]);
        setError(t('KESALAHAN KONEKSI: PERIKSA BAHWA BACKEND BERJALAN DI http://localhost:3000'));
      }
    } finally {
      setLoading(false);
    }
  }, [kategori, selectedJob]);
  
  // Search master jobs when input changes
  useEffect(() => {
    const searchTimer = setTimeout(() => {
      if (inputValue.length >= 2) {
        searchMasterJobs(inputValue);
      } else if (inputValue.length === 0 && options.length === 0) {
        loadAllMasterJobs();
      }
    }, 500);
    
    return () => clearTimeout(searchTimer);
  }, [inputValue, kategori, options.length, loadAllMasterJobs, searchMasterJobs]);
  
  const handleChange = (event, newValue) => {
    console.log('Selected new master job:', newValue);
    setSelectedJob(newValue);
    onChange(newValue ? newValue.id : null);
    
    // Clear any error message when a selection is made
    if (newValue) {
      setError(null);
    }
  };
  
  return (
    <>
      <Autocomplete
        id="master-job-selector"
        fullWidth
        options={options}
        filterOptions={(x) => x} // Disable client-side filtering
        getOptionLabel={(option) => `${option.kode_pekerjaan}: ${option.nama_pekerjaan}`}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {option.kode_pekerjaan}: {option.nama_pekerjaan}
              </Typography>
              <Box display="flex" alignItems="center" mt={0.5}>
                <Chip 
                  label={option.kategori} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                {option.volume_default > 0 && (
                  <Chip 
                    label={`${option.volume_default} ${option.satuan}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </li>
        )}
        onChange={handleChange}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('CARI ITEM PEKERJAAN MASTER')}
            variant="outlined"
            placeholder={t('KETIK MINIMAL 2 KARAKTER...')}
            helperText={loading ? t('MEMUAT...') : t('CARI BERDASARKAN KODE ATAU NAMA ITEM PEKERJAAN')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              )
            }}
          />
        )}
        value={selectedJob}
        loading={loading}
        loadingText={t('MEMUAT ITEM PEKERJAAN...')}
        noOptionsText={t('TIDAK ADA ITEM PEKERJAAN YANG COCOK DITEMUKAN')}
      />
      
      {error && !selectedJob && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      
      {options.length === 0 && !loading && !error && !selectedJob && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('TIDAK ADA ITEM PEKERJAAN MASTER YANG TERSEDIA. ANDA MUNGKIN PERLU MEMBUAT BEBERAPA TERLEBIH DAHULU DI DAFTAR MASTER ITEM PEKERJAAN.')}
        </Alert>
      )}
      
      {selectedJob && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {t('DIPILIH')}: {selectedJob.kode_pekerjaan}: {selectedJob.nama_pekerjaan}
        </Alert>
      )}
    </>
  );
};

export default MasterJobSelector; 